import React, { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { getZonedDate } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { ActionPanelCard } from '@eika/action-panel-card';
import { Button } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useAccountNumber } from '../../fetchHooks/useAccount';
import useLoanRepaymentPlan from '../../fetchHooks/useLoanRepaymentPlan';
import { useLoanDetails } from '../../fetchHooks/useLoans';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';
import EditLoanDebitAccountModal from './components/EditLoanDebitAccountModal';
import EditLoanPaymentDay from './components/EditLoanPaymentDayModal';
import getYearOrMonthIntervalText from './getYearOrMonthIntervalText';

import './EditLoan.scss';

const EditLoan: React.FC = () => {
    const translated = useTranslation();
    const accountId = useAccountId();
    const { featureToggles } = useConfig();

    const { data: loanDetails, isLoading, isSuccess, error, invalidateQuery } = useLoanDetails(accountId);
    const { data: loanRepaymentPlan } = useLoanRepaymentPlan(accountId);

    const getMonthOrYearText = useMemo(() => getYearOrMonthIntervalText(translated), [translated]);

    const eachYearOrMonthText = useMemo(
        () => getMonthOrYearText(loanDetails, loanRepaymentPlan),
        [loanRepaymentPlan, loanDetails, getMonthOrYearText],
    );

    const { data: account } = useAccountNumber(loanDetails?.debitAccountNumber);

    const [paymentDateModalOpen, setPaymentDateModalOpen] = useState(false);
    const [debitAccountModalOpen, setDebitAccountModalOpen] = useState(false);

    const targetAccountId = accountId ?? loanDetails?.accountId;

    const onSubmit = () => {
        setPaymentDateModalOpen(false);
        setDebitAccountModalOpen(false);
        invalidateQuery();
    };

    if (error) {
        return (
            <ErrorCardWrapper exception={error} defaultError={translated.loanError}>
                <Button onClick={invalidateQuery}>{translated.tryAgain}</Button>
            </ErrorCardWrapper>
        );
    }

    if (isLoading) {
        return (
            <div className="edit-loan__wrapper">
                <Skeleton height={88} style={{ marginBottom: 16 }} />
                <Skeleton height={88} />
            </div>
        );
    }

    if (!loanDetails?.nextPaymentDate && !loanDetails?.debitAccountNumber) {
        return isSuccess ? (
            <div className="edit-loan__wrapper">
                <h2>{translated.loanDetails.noLoan}</h2>
            </div>
        ) : (
            <></>
        );
    }

    return featureToggles.enableEditLoan ? (
        <div className="edit-loan__wrapper">
            {loanDetails.nextPaymentDay && targetAccountId && loanDetails.nextPaymentDate && (
                <>
                    <ActionPanelCard
                        title={translated.loanEdit.editDay}
                        icon={IconTypes.PENN_24}
                        onClick={() => setPaymentDateModalOpen(true)}
                        id="edit-payment-day-loan"
                        subText={eachYearOrMonthText}
                        data-testid="edit-loan-payment-date"
                    />
                    <EditLoanPaymentDay
                        accountId={targetAccountId}
                        currentDay={loanDetails.nextPaymentDay}
                        nextDate={getZonedDate(loanDetails.nextPaymentDate)}
                        show={paymentDateModalOpen}
                        setShow={setPaymentDateModalOpen}
                        onSubmit={onSubmit}
                    />
                </>
            )}
            {loanDetails.debitAccountNumber && targetAccountId && (
                <>
                    <ActionPanelCard
                        title={translated.loanEdit.editAccountToPayFrom}
                        icon={IconTypes.PENN_24}
                        onClick={() => setDebitAccountModalOpen(true)}
                        id="edit-debit-loan-account"
                        subText={`${account?.alias ?? ''} (${formatAccountNumber(loanDetails.debitAccountNumber)})`}
                        data-testid="edit-loan-payment-account"
                    />
                    <EditLoanDebitAccountModal
                        accountId={targetAccountId}
                        currentAccount={loanDetails.debitAccountNumber}
                        show={debitAccountModalOpen}
                        setShow={setDebitAccountModalOpen}
                        onSubmit={onSubmit}
                    />
                </>
            )}
        </div>
    ) : (
        <></>
    );
};

export default EditLoan;
